import React from 'react'
import { 
  Layout, 
  Row, 
  Col, 
  Card, 
  Button, 
  Typography, 
  Space,
  Spin,
  Empty,
  Breadcrumb,
  Tag,
  Avatar,
  Divider,

  message
} from 'antd'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  HomeOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  EyeOutlined,
  HeartOutlined,
  ShareAltOutlined,
  ClockCircleOutlined,
  TagsOutlined
} from '@ant-design/icons'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { articlesApi, siteSettingsApi } from '../../services/api'

const { Content } = Layout
const { Title, Text, Paragraph } = Typography

const BlogDetailPage: React.FC = () => {
  const { i18n, t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const currentLang = i18n.language as 'en' | 'zh'

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const settings = siteSettingsData?.settings

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }

  // Fetch article details
  const { data: articleData, isLoading, error } = useQuery({
    queryKey: ['article', id],
    queryFn: () => articlesApi.getById(id!),
    enabled: !!id
  })



  const article = articleData?.article

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article?.title.en,
        text: article?.excerpt.en,
        url: window.location.href,
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      message.success(t('messages.success.linkCopied'))
    }
  }

  const formatDate = (dateString: string) => {
    const locale = i18n.language.startsWith('zh') ? 'zh-HK' : 'en-US'
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getReadingTime = (content: string) => {
    const words = content?.split(' ').length || 0
    const minutes = Math.ceil(words / 200)
    return t('blog.meta.readingTime', { minutes })
  }

  if (isLoading) {
    return (
      <Content>
        <div className="container mx-auto px-gutter py-20">
          <div className="text-center">
            <Spin size="large" />
            <div className="mt-4 text-gray-500">Loading article...</div>
          </div>
        </div>
      </Content>
    )
  }

  if (error || !article) {
    return (
      <Content>
        <div className="container mx-auto px-gutter py-20">
          <div className="text-center">
            <Empty description="Article not found" />
            <Link to="/blog">
              <Button type="primary" className="mt-4">
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
      </Content>
    )
  }

  return (
    <>
      <Helmet>
        <title>{getLocalizedText(article.title)} - {getLocalizedText(settings?.siteName, 'MedCareHub')} Blog</title>
        <meta name="description" content={article.excerpt[currentLang] || article.excerpt.en} />
      </Helmet>

      <Content>
        {/* Breadcrumb */}
        <div className="bg-gray-50 py-4">
          <div className="container mx-auto px-gutter">
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/">
                  <HomeOutlined /> {t('navigation.home')}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/blog">{t('navigation.blog')}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{article.title[currentLang] || article.title.en}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>

        <div className="container mx-auto px-gutter py-12">
          <Row>
            {/* Main Content */}
            <Col xs={24}>
              <article className="article-content">
                {/* Article Header */}
                <div className="article-header mb-8">
                  {article.category && (
                    <Tag color="blue" className="mb-4">{article.category[currentLang] || article.category.en}</Tag>
                  )}
                  
                  <Title level={1} className="text-4xl font-bold mb-6">
                    {article.title[currentLang] || article.title.en}
                  </Title>

                  <Paragraph className="text-xl text-gray-600 mb-6">
                    {article.excerpt[currentLang] || article.excerpt.en}
                  </Paragraph>

                  {/* Article Meta */}
                  <div className="article-meta mb-6">
                    <Space split={<Divider type="vertical" />} wrap>
                      <Space>
                        <Avatar icon={<UserOutlined />} />
                        <div>
                          <Text strong>{article.author.name}</Text>
                          {article.author.title && (
                            <div className="text-sm text-gray-500">{article.author.title}</div>
                          )}
                        </div>
                      </Space>
                      <Text className="text-gray-500">
                        <CalendarOutlined /> {formatDate(article.publishedAt)}
                      </Text>
                      <Text className="text-gray-500">
                        <ClockCircleOutlined /> {getReadingTime((article.content?.[currentLang] || article.content?.en || ''))}
                      </Text>
                      <Text className="text-gray-500">
                        <EyeOutlined /> {t('blog.meta.views', { count: article.views || 0 })}
                      </Text>
                    </Space>
                  </div>

                  {/* Action Buttons */}
                  <Space className="mb-8">
                    <Button icon={<HeartOutlined />}>
                      {t('common.like', 'Like')} ({article.likes || 0})
                    </Button>
                    <Button icon={<ShareAltOutlined />} onClick={handleShare}>
                      {t('common.share')}
                    </Button>
                  </Space>
                </div>

                {/* Featured Image */}
                {(() => {
                  const src = article.featuredImage?.[currentLang]?.url || article.featuredImage?.url;
                  const alt = article.featuredImage?.[currentLang]?.alt || article.featuredImage?.alt?.[currentLang] || article.title[currentLang] || article.title.en;
                  if (!src) return null;
                  return (
                    <div className="featured-image mb-8">
                      <img
                        src={src}
                        alt={alt}
                        className="w-full max-h-screen object-contain rounded-lg"
                      />
                    </div>
                  );
                })()}

                {/* Article Content */}
                <div className="article-body">
                  <div className="prose prose-lg max-w-none prose-img:object-contain prose-img:max-h-screen prose-img:w-full">
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: (article.content?.[currentLang] || article.content?.en || '').replace(/\n/g, '<br/>') || (article.excerpt[currentLang] || article.excerpt.en) 
                      }} 
                    />
                  </div>
                </div>

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                  <div className="article-tags mt-8 pt-8 border-t">
                    <Space wrap>
                      <Text className="text-gray-500">
                        <TagsOutlined /> {t('blog.meta.tags')}:
                      </Text>
                      {article.tags.map((tag: any, index: number) => {
                        const tagText = typeof tag === 'string' ? tag : getLocalizedText(tag);
                        return (
                          <Tag key={index} className="cursor-pointer">
                            <Link to={`/blog?tag=${tagText}`}>{tagText}</Link>
                          </Tag>
                        );
                      })}
                    </Space>
                  </div>
                )}

                {/* Author Bio */}
                <Card className="mt-8 bg-gray-50">
                  <div className="flex items-start space-x-4">
                    <Avatar size={64} icon={<UserOutlined />} />
                    <div className="flex-1">
                      <Title level={4} className="mb-2">{article.author.name}</Title>
                      {article.author.title && (
                        <Text className="text-gray-600 block mb-2">{article.author.title}</Text>
                      )}
                      <Paragraph className="text-gray-600 mb-0">
                        {getLocalizedText(article.author.bio) || `${article.author.name} is a healthcare professional contributing to MedCareHub's blog with expertise in medical devices and health monitoring.`}
                      </Paragraph>
                    </div>
                  </div>
                </Card>


              </article>
            </Col>
          </Row>
        </div>
      </Content>
    </>
  )
}

export default BlogDetailPage
